import {Component, OnInit} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {EMPTY} from 'rxjs';
import {AutoScraperBackendService} from "../auto-scraper-backend.service";
import {environment} from '../../environments/environment';

/** One editable row of basic_supplier_info.currency_mapping. */
export interface CurrencyMappingRow {
  currency_text: string
  currency: string
}

/** One editable row of basic_supplier_info.vat_mapping; vat_included stays null until chosen. */
export interface VatMappingRow {
  vat_text: string
  vat_included: boolean | null
}

/** One editable row of basic_supplier_info.price_status_mapping; status stays null until chosen. */
export interface PriceStatusMappingRow {
  text: string
  status: number | null
}

/** The PriceStatus codes a mapping may target, as AutoScraper's MAPPABLE_PRICE_STATUSES. */
export const PRICE_STATUS_OPTIONS: {value: number, label: string}[] = [
  {value: -2, label: 'price on request'},
  {value: -4, label: 'login'},
  {value: -3, label: 'used'},
  {value: -1, label: 'client protection'},
];

@Component({
    selector: 'app-meta',
    templateUrl: './meta.component.html',
    styleUrls: ['./meta.component.css'],
    standalone: false
})
export class MetaComponent implements OnInit {
  constructor(public data: AutoScraperBackendService, private http: HttpClient) {
  }

  currencyMappingRows: CurrencyMappingRow[] = [];
  currencyMappingError = '';
  currencyPreviewText = '';
  currencyPreviewResult = '';

  priceStatusMappingRows: PriceStatusMappingRow[] = [];
  priceStatusMappingError = '';
  priceStatusPreviewText = '';
  priceStatusPreviewResult = '';
  readonly priceStatusOptions = PRICE_STATUS_OPTIONS;

  vatMappingRows: VatMappingRow[] = [];
  vatMappingError = '';
  vatPreviewText = '';
  vatPreviewResult = '';

  ngOnInit(): void {
    const info = this.data.supplier_implementation.basic_supplier_info;
    const mapping = info.currency_mapping ?? {};
    this.currencyMappingRows = Object.entries(mapping).map(([currency_text, currency]) => ({currency_text, currency}));
    const vatMapping = info.vat_mapping ?? {};
    this.vatMappingRows = Object.entries(vatMapping).map(([vat_text, vat_included]) => ({vat_text, vat_included}));
    const priceStatusMapping = info.price_status_mapping ?? {};
    this.priceStatusMappingRows = Object.entries(priceStatusMapping).map(([text, status]) => ({text, status}));
  }

  /** Appends an empty price status mapping row; it is only written once text and status are set. */
  addPriceStatusMappingRow() {
    this.priceStatusMappingRows.push({text: '', status: null});
  }

  removePriceStatusMappingRow(idx: number) {
    this.priceStatusMappingRows.splice(idx, 1);
    this.syncPriceStatusMapping();
  }

  /**
   * Writes the complete rows back to basic_supplier_info.price_status_mapping (persisted with the next save).
   * Texts are trimmed, incomplete rows skipped, duplicate texts reported and only the first one kept.
   */
  syncPriceStatusMapping() {
    const mapping: Record<string, number> = {};
    const duplicates: string[] = [];
    for (const row of this.priceStatusMappingRows) {
      const text = row.text.trim();
      if (!text || typeof row.status !== 'number') continue;
      if (text in mapping) {
        duplicates.push(text);
        continue;
      }
      mapping[text] = row.status;
    }
    this.priceStatusMappingError = duplicates.length ? `Duplicate text: ${duplicates.join(', ')}` : '';
    this.data.supplier_implementation.basic_supplier_info.price_status_mapping = mapping;
    this.priceStatusPreviewResult = '';
  }

  /** Resolves priceStatusPreviewText with the current mapping and the built-in keywords via /get_parsed_price_status. */
  previewPriceStatus() {
    const info = this.data.supplier_implementation.basic_supplier_info;
    this.priceStatusPreviewResult = 'Working on it';
    this.http.post<number | null | string>(environment.apiUrl + '/get_parsed_price_status', {
      price_status_text: this.priceStatusPreviewText || null,
      price_status_mapping: info.price_status_mapping ?? {},
    }, {headers: new HttpHeaders({'auth': this.data.token})}).pipe(
      catchError(() => { this.priceStatusPreviewResult = 'Error'; return EMPTY; })
    ).subscribe(res => {
      if (typeof res === 'string') { this.priceStatusPreviewResult = res; return; }
      this.priceStatusPreviewResult = typeof res === 'number'
        ? PRICE_STATUS_OPTIONS.find(o => o.value === res)?.label ?? String(res)
        : 'no status (the text is parsed as a price)';
    });
  }

  /** Appends an empty mapping row; it is only written to the implementation once text and currency are set. */
  addCurrencyMappingRow() {
    this.currencyMappingRows.push({currency_text: '', currency: ''});
  }

  removeCurrencyMappingRow(idx: number) {
    this.currencyMappingRows.splice(idx, 1);
    this.syncCurrencyMapping();
  }

  /**
   * Writes the complete rows back to basic_supplier_info.currency_mapping (persisted with the next save).
   * Texts are trimmed, incomplete rows skipped, duplicate texts reported and only the first one kept.
   */
  syncCurrencyMapping() {
    const mapping: Record<string, string> = {};
    const duplicates: string[] = [];
    for (const row of this.currencyMappingRows) {
      const text = row.currency_text.trim();
      if (!text || !row.currency) continue;
      if (text in mapping) {
        duplicates.push(text);
        continue;
      }
      mapping[text] = row.currency;
    }
    this.currencyMappingError = duplicates.length ? `Duplicate currency text: ${duplicates.join(', ')}` : '';
    this.data.supplier_implementation.basic_supplier_info.currency_mapping = mapping;
    this.currencyPreviewResult = '';
  }

  /** Resolves currencyPreviewText with the current mapping and default currency via /get_parsed_currency. */
  previewCurrency() {
    const info = this.data.supplier_implementation.basic_supplier_info;
    this.currencyPreviewResult = 'Working on it';
    this.http.post<string | null>(environment.apiUrl + '/get_parsed_currency', {
      currency_text: this.currencyPreviewText || null,
      currency_mapping: info.currency_mapping ?? {},
      default_currency: info.currency || null,
    }, {headers: new HttpHeaders({'auth': this.data.token})}).pipe(
      catchError(() => { this.currencyPreviewResult = 'Error'; return EMPTY; })
    ).subscribe(res => {
      this.currencyPreviewResult = res ?? 'unknown';
    });
  }

  /** Appends an empty VAT mapping row; it is only written to the implementation once text and status are set. */
  addVatMappingRow() {
    this.vatMappingRows.push({vat_text: '', vat_included: null});
  }

  removeVatMappingRow(idx: number) {
    this.vatMappingRows.splice(idx, 1);
    this.syncVatMapping();
  }

  /**
   * Writes the complete rows back to basic_supplier_info.vat_mapping (persisted with the next save).
   * Texts are trimmed, incomplete rows skipped, duplicate texts reported and only the first one kept.
   */
  syncVatMapping() {
    const mapping: Record<string, boolean> = {};
    const duplicates: string[] = [];
    for (const row of this.vatMappingRows) {
      const text = row.vat_text.trim();
      if (!text || typeof row.vat_included !== 'boolean') continue;
      if (text in mapping) {
        duplicates.push(text);
        continue;
      }
      mapping[text] = row.vat_included;
    }
    this.vatMappingError = duplicates.length ? `Duplicate VAT text: ${duplicates.join(', ')}` : '';
    this.data.supplier_implementation.basic_supplier_info.vat_mapping = mapping;
    this.vatPreviewResult = '';
  }

  /**
   * Resolves vatPreviewText with the current mapping via /get_parsed_vat. The default is not sent, so a text
   * that says nothing shows up as the supplier default instead of looking like a match.
   */
  previewVat() {
    const info = this.data.supplier_implementation.basic_supplier_info;
    this.vatPreviewResult = 'Working on it';
    this.http.post<boolean | null>(environment.apiUrl + '/get_parsed_vat', {
      vat_text: this.vatPreviewText || null,
      vat_mapping: info.vat_mapping ?? {},
      default_vat_included: null,
    }, {headers: new HttpHeaders({'auth': this.data.token})}).pipe(
      catchError(() => { this.vatPreviewResult = 'Error'; return EMPTY; })
    ).subscribe(res => {
      const label = (v: boolean | null | undefined) => v === true ? 'VAT included' : v === false ? 'VAT excluded' : 'unknown';
      this.vatPreviewResult = typeof res === 'boolean'
        ? label(res)
        : `supplier default (${label(info.vat_included_on_displayed_price)})`;
    });
  }

  countries = ["AFG",  "ALA",  "ALB",  "DZA",  "ASM",  "AND",  "AGO",  "AIA",  "ATA",  "ATG",  "ARG",  "ARM",  "ABW",  "AUS",  "AUT",  "AZE",  "BHS",  "BHR",  "BGD",  "BRB",  "BLR",  "BEL",  "BLZ",  "BEN",  "BMU",  "BTN",  "BOL",  "BES",  "BIH",  "BWA",  "BVT",  "BRA",  "IOT",  "BRN",  "BGR",  "BFA",  "BDI",  "KHM",  "CMR",  "CAN",  "CPV",  "CYM",  "CAF",  "TCD",  "CHE",  "CHL",  "CHN",  "CXR",  "CCK",  "COL",  "COM",  "COG",  "COD",  "COK",  "CRI",  "CIV",  "HRV",  "CUB",  "CUW",  "CYP",  "CZE",  "DNK",  "DJI",  "DMA",  "DOM",  "ECU",  "EGY",  "SLV",  "GNQ",  "ERI",  "EST",  "ETH",  "FLK",  "FRO",  "FJI",  "FIN",  "FRA",  "GUF",  "PYF",  "ATF",  "GAB",  "GMB",  "GEO",  "DEU",  "GHA",  "GIB",  "GRC",  "GRL",  "GRD",  "GLP",  "GUM",  "GTM",  "GGY",  "GIN",  "GNB",  "GUY",  "HTI",  "HMD",  "VAT",  "HND",  "HKG", "HUN", "ISL", "IND", "IDN", "IRN", "IRQ", "IRL", "IMN", "ISR", "ITA", "JAM", "JPN", "JEY", "JOR", "KAZ", "KEN", "KIR", "PRK", "KOR", "KWT", "KGZ", "LAO", "LVA", "LBN", "LSO", "LBR", "LBY", "LIE", "LTU", "LUX", "MAC", "MKD", "MDG", "MWI", "MYS", "MDV", "MLI", "MLT", "MHL", "MTQ", "MRT", "MUS", "MYT", "MEX", "FSM", "MDA", "MCO", "MNG", "MNE", "MSR", "MAR", "MOZ", "MMR", "NAM", "NRU", "NPL", "NLD", "NCL", "NZL", "NIC", "NER", "NGA", "NIU", "NFK", "MNP", "NOR", "OMN", "PAK", "PLW", "PSE", "PAN", "PNG", "PRY", "PER", "PHL", "PCN", "POL", "PRT", "PRI", "QAT", "REU", "ROU", "RUS", "RWA", "BLM", "SHN", "KNA", "LCA", "MAF", "SPM", "VCT", "WSM", "SMR", "STP", "SAU", "SEN", "SRB", "SYC", "SLE", "SGP", "SXM", "SVK", "SVN", "SLB", "SOM", "ZAF", "SGS", "SSD", "ESP", "LKA", "SDN", "SUR", "SJM", "SWZ", "SWE", "SYR", "TWN", "TJK", "TZA", "THA", "TLS", "TGO", "TKL", "TON", "TTO", "TUN", "TUR", "TKM", "TCA", "TUV", "UGA", "UKR", "ARE", "GBR", "USA", "UMI", "URY", "UZB", "VUT", "VEN", "VNM", "VGB", "VIR", "WLF", "ESH", "YEM", "ZMB", "ZWE", "ANT", "XKX"].sort()
  currencies =  ["BGN",  "BYN",  "CHF",  "CNY",  "EUR",  "GBP",  "HKD",  "PLN",  "RUB",  "USD",  "BRL",  "SEK",  "CZK",  "HUF",  "HRK",  "DKK",  "NOK",  "RON",  "JPY",  "AUD",  "TWD",  "INR",  "ZAR",  "MYR",  "PHP",  "SGD",  "KRW",  "THB",  "NZD",  "ILS",  "TRY",  "CAD",  "IDR",  "UAH",  "ARS",  "VND",  "MXN",  "SAR",  "AED",  "AFN",  "ALL",  "DZD",  "AOA",  "XCD",  "AMD",  "AWG",  "AZN",  "BSD",  "BHD",  "BDT",  "BBD",  "BZD",  "XOF",  "BMD",  "BTN",  "BOB",  "BOV",  "BAM",  "BWP",  "BND",  "BIF",  "CVE",  "KHR",  "XAF",  "KYD",  "CLF",  "CLP",  "COP",  "COU",  "KMF",  "CDF",  "CRC",  "CUC",  "CUP",  "ANG",  "DJF",  "DOP",  "EGP",  "SVC",  "ERN",  "ETB",  "FKP",  "FJD",  "XPF",  "GMD",  "GEL",  "GHS",  "GIP",  "GTQ",  "GNF",  "GYD",  "HTG",  "HNL",  "ISK",  "XDR",  "IRR",  "IQD",  "JMD",  "JOD",  "KZT",  "KES", "KPW", "KWD", "KGS", "LAK", "LBP", "LSL", "LRD", "LYD", "MOP", "MGA", "MWK", "MVR", "MRU", "MUR", "XUA", "MXV", "MDL", "MNT", "MAD", "MZN", "MMK", "NAD", "NPR", "NIO", "NGN", "OMR", "PKR", "PAB", "PGK", "PYG", "PEN", "QAR", "MKD", "RWF", "SHP", "WST", "STN", "RSD", "SCR", "SLE", "XSU", "SBD", "SOS", "SSP", "LKR", "SDG", "SRD", "SZL", "CHE", "CHW", "SYP", "TJS", "TZS", "TOP", "TTD", "TND", "TMT", "UGX", "USN", "UYI", "UYU", "UZS", "VUV", "VEF", "VED", "YER", "ZMW", "ZWL"].sort()
  languages = ['CA', 'AK', 'KA', 'TI', 'IK', 'KN', 'NO', 'RU', 'WA', 'LO', 'CE', 'IE', 'IG', 'ID', 'MK', 'PL', 'GU', 'EL', 'NG', 'NR', 'ET', 'SA', 'KV', 'JA', 'BA', 'CS', 'SV', 'KM', 'NA', 'SC', 'HA', 'HZ', 'FI', 'AY', 'HY', 'TH', 'KJ', 'OR', 'CV', 'KO', 'UR', 'GA', 'XH', 'TO', 'HT', 'KG', 'MH', 'SO', 'FO', 'KU', 'WO', 'LT', 'JV', 'MY', 'SE', 'GL', 'BS', 'AE', 'KW', 'BO', 'BG', 'OC', 'TG', 'DA', 'FY', 'SQ', 'SW', 'CO', 'KR', 'LB', 'ZU', 'ES', 'IO', 'NN', 'HR', 'SR', 'GV', 'AV', 'CH', 'TY', 'FF', 'CR', 'DV', 'MG', 'TW', 'SI', 'ZH', 'NE', 'SD', 'AA', 'VE', 'MI', 'IU', 'FR', 'IT', 'LU', 'SN', 'CY', 'OJ', 'GD', 'MS', 'UZ', 'VO', 'UK', 'AM', 'NV', 'RM', 'HU', 'ML', 'EE', 'YO', 'EO', 'KL', 'KS', 'TE', 'EN', 'PS', 'NB', 'HE', 'LG', 'NY', 'GN', 'AZ', 'AR', 'AS', 'DE', 'IS', 'TT', 'VI', 'PI', 'ST', 'AB', 'SK', 'TR', 'TN', 'NL', 'FA', 'RN', 'SL', 'II', 'MT', 'RO', 'SG', 'SU', 'LI', 'MN', 'AN', 'HI', 'AF', 'TS', 'LN', 'OM', 'KY', 'LA', 'TA', 'QU', 'YI', 'BR', 'RW', 'BI', 'BM', 'KI', 'KK', 'TL', 'BE', 'UG', 'PA', 'BH', 'MR', 'TK', 'BN', 'LV', 'SM', 'CU', 'SS', 'PT', 'EU', 'FJ', 'ND', 'ZA', 'OS', 'HO', 'DZ', 'IA'].sort()
  decimal_separator = [".", ","]

}

